import {useCallback, useEffect, useLayoutEffect, useRef, useState} from "react";
import css from "../views/LiveRefresh/LiveRefresh.less";

export const HIGHLIGHT_MS = 3000;
export const GONE_HINT_MS = 6000;

export const rowId = (row: {id: string}): string => row.id;

type Snapshot = {scope: string; rows: Map<string, string>; total?: number};

const snapshot = <T>(rows: ReadonlyArray<T>, getKey: (row: T) => string): Map<string, string> => new Map(rows.map((row) => [getKey(row), JSON.stringify(row)]));

/**
 * Markiert Zeilen, die eine fremde Änderung (Live-Aktualisierung) neu gebracht oder verändert hat,
 * für 3 s und zählt Zeilen, die dabei aus der Liste gefallen sind (Hinweis 6 s).
 *
 * `track(refetch, foreign)` ersetzt den Refetch für `useLiveRefresh`: nur bei `foreign` wird der Stand
 * davor gemerkt und mit den nächsten Zeilen verglichen. Eigene Aktionen, Filter und Seitenwechsel laufen
 * nicht über `track` und markieren daher nichts. `scope` (z. B. Tab + Seite) verwirft den Vergleich, wenn
 * der Nutzer währenddessen umschaltet. Als Änderungsstempel dient die ganze Zeile (JSON), damit auch
 * Änderungen an verknüpften Daten (Vorschlag, Bucher) zählen.
 *
 * `total` (Gesamtzahl über alle Seiten) gehört bei paginierten Listen dazu: Verschwunden ist dann nur,
 * um wie viel die Gesamtzahl gesunken ist, und neu nur, was bei gewachsener Liste dazukam. Zeilen, die
 * nur zwischen den Seiten verrutscht sind, zählen weder als erledigt noch als neu.
 */
export const useRowChangeHighlight = <T>(rows: ReadonlyArray<T>, getKey: (row: T) => string, scope = "", total?: number) => {
    const [changed, setChanged] = useState<ReadonlySet<string>>(new Set());
    const [goneCount, setGoneCount] = useState(0);
    const before = useRef<Snapshot | undefined>();
    const current = useRef({rows, getKey, scope, total});
    current.current = {rows, getKey, scope, total};

    const track = useCallback(async <R>(refetch: () => Promise<R>, foreign: boolean): Promise<R> => {
        const {rows: currentRows, getKey: key, scope: currentScope, total: currentTotal} = current.current;
        if (foreign) before.current = {scope: currentScope, rows: snapshot(currentRows, key), total: currentTotal};
        try {
            return await refetch();
        } finally {
            // Gleiche Daten erzeugen keinen neuen Render; dann verfällt der Stand hier.
            before.current = undefined;
        }
    }, []);

    // Layout-Effekt: läuft im selben Commit wie die neuen Zeilen, also vor dem `finally` oben.
    useLayoutEffect(() => {
        const previous = before.current;
        if (!previous) return;
        before.current = undefined;
        if (previous.scope !== scope) return;

        const next = snapshot(rows, getKey);
        const paged = previous.total !== undefined && total !== undefined;
        // Bei Seiten ist eine unbekannte Zeile nur neu, wenn die Liste gewachsen ist; sonst ist sie bloß
        // von der Nachbarseite nachgerückt. ponytail: wächst die Liste und rückt zugleich etwas nach,
        // leuchtet der Nachrücker mit (selten).
        const newCounts = !paged || (total as number) > (previous.total as number);
        const changedKeys: Array<string> = [];
        next.forEach((json, key) => {
            const old = previous.rows.get(key);
            if (old === undefined ? newCounts : old !== json) changedKeys.push(key);
        });
        let gone = 0;
        if (previous.total !== undefined && total !== undefined) {
            gone = Math.max(0, previous.total - total);
        } else {
            previous.rows.forEach((_, key) => !next.has(key) && (gone += 1));
        }

        if (changedKeys.length) setChanged(new Set(changedKeys));
        if (gone) setGoneCount(gone);
    }, [rows]);

    // Tab-, Seiten- oder Filterwechsel: Markierung und Hinweis gehören zur alten Ansicht.
    useEffect(() => {
        setChanged(new Set());
        setGoneCount(0);
    }, [scope]);

    useEffect(() => {
        if (!changed.size) return;
        const timer = setTimeout(() => setChanged(new Set()), HIGHLIGHT_MS);

        return () => clearTimeout(timer);
    }, [changed]);

    useEffect(() => {
        if (!goneCount) return;
        const timer = setTimeout(() => setGoneCount(0), GONE_HINT_MS);

        return () => clearTimeout(timer);
    }, [goneCount]);

    const rowClassName = useCallback((row: T): string => (changed.has(getKey(row)) ? css.liveChanged : ""), [changed, getKey]);

    return {track, rowClassName, goneCount, goneHintClassName: css.goneHint};
};
